import { Box, Container, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { Header } from "../../../components/Header";
import UserAvatar from "../../../components/UserAvatar";
import { colorGrid } from "../../../theme";
import { useUserData } from "../../../user";
import { useUserActivity } from "../../../utils/contexts/users/userActivityContext";
import { useLocalState } from "../../../utils/hooks/global/useLocalStorage";
import CustomTabsTable from "../../global/CustomTabsTable";
import { Loading } from "../../global/Loading";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import RemoveModeratorOutlinedIcon from "@mui/icons-material/RemoveModeratorOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import UserActionButton from "./UserActionButton";

const roleOf = (user) =>
  user.isSuperadmin ? "Superadmin" : user.isAdmin ? "Admin" : "User";

const ROLE_COLOR = { Superadmin: "blue", Admin: "yellow", User: "gray" };

function AdminUsers() {
  const [jwt] = useLocalState(null, "jwt");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const { userStatus } = useUserActivity();
  const currentUser = useUserData();

  const handleUserChange = (updated) => {
    setUsers((prev) =>
      prev.map((user) =>
        user.id === updated.id
          ? {
              ...user,
              isAdmin: updated.isAdmin,
              archivedAt: updated.archivedAt,
              isOnline: updated.isOnline,
            }
          : user
      )
    );
  };

  useEffect(() => {
    if (userStatus) {
      const newAllUsers = users.map((user) => {
        if (user.id === userStatus?.userId) {
          return {
            ...user,
            isOnline: userStatus?.isOnline,
            lastActive: userStatus?.lastActive,
          };
        }
        return user;
      });
      setUsers(newAllUsers);
    }
  }, [userStatus]);

  // Archived users (people who left) only show in their own tab
  const current = (users) => users.filter((user) => !user.archivedAt);
  const tabsFunctions = {
    all: {
      func: function (data) {
        return current(data);
      },
      label: "All",
      color: "default",
    },
    isOnline: {
      func: function (data) {
        return current(data).filter((user) => user.isOnline);
      },
      label: "Active",
      color: "green",
    },
    isAdmin: {
      func: function (values) {
        return current(values).filter((user) => user.isAdmin);
      },
      label: "Admin",
      color: "yellow",
    },
    offline: {
      func: function (values) {
        return current(values).filter((user) => !user.isOnline);
      },
      label: "Offline",
      color: "gray",
    },
    archived: {
      func: function (values) {
        return values.filter((user) => user.archivedAt);
      },
      label: "Archived",
      color: "gray",
    },
  };

  useEffect(() => {
    fetch(`${backendUrl}/users?includeProfiles=true`, {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: "Bearer " + jwt,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setUsers(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error("There was an error!", error);
      });
  }, [jwt, backendUrl]);

  const columns = [
    {
      field: "displayName",
      headerName: "Name",
      renderCell: (params) => {
        return (
          <Box
            key={params.id}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <UserAvatar
              name={params.row.displayName}
              size={28}
              sx={{ mr: 1.25 }}
            />
            {params.formattedValue}
          </Box>
        );
      },
      flex: 1,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
    },
    {
      field: "lastActive",
      headerName: "Last seen",
      flex: 1,
      valueFormatter: (params) => {
        const date = new Date(params.value);
        const formattedDate = date.toLocaleDateString(undefined, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "UTC",
        });
        return formattedDate;
      },
    },
    {
      field: "role",
      headerName: "Role",
      flex: 0.6,
      minWidth: 110,
      valueGetter: (params) => roleOf(params.row),
      renderCell: (params) => {
        const color = colorGrid[ROLE_COLOR[params.value]];
        return (
          <Box
            component="span"
            sx={{
              bgcolor: color.background,
              color: color.text,
              borderRadius: "4px",
              px: 0.75,
              py: 0.25,
              fontWeight: 700,
              fontSize: 12,
            }}
          >
            {params.value}
          </Box>
        );
      },
    },
    // Only superadmins change roles and archive users; superadmins
    // themselves and your own account are managed elsewhere
    ...(currentUser?.isSuperadmin
      ? [
          {
            field: "actions",
            headerName: "",
            sortable: false,
            filterable: false,
            flex: 0.5,
            minWidth: 100,
            align: "right",
            renderCell: (params) => {
              const user = params.row;
              if (user.isSuperadmin || user.id === currentUser.id) return null;
              if (user.archivedAt) {
                return (
                  <UserActionButton
                    user={user}
                    path="archive"
                    body={{ archived: false }}
                    label="Restore"
                    icon={UnarchiveOutlinedIcon}
                    title={`Restore ${user.displayName}?`}
                    text="They can use the app again and show up in member pickers."
                    onChange={handleUserChange}
                  />
                );
              }
              return (
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <UserActionButton
                    user={user}
                    path="role"
                    body={{ isAdmin: !user.isAdmin }}
                    label={user.isAdmin ? "Remove admin" : "Make admin"}
                    icon={
                      user.isAdmin
                        ? RemoveModeratorOutlinedIcon
                        : AdminPanelSettingsOutlinedIcon
                    }
                    title={`${user.isAdmin ? "Remove" : "Make"} ${
                      user.displayName
                    } ${user.isAdmin ? "as admin" : "an admin"}?`}
                    text={`${
                      user.isAdmin
                        ? "They keep their account but lose access to the admin pages and actions."
                        : "Admins can manage entities, channels, announcements, partners and status checks."
                    } The change applies from their next action in the app.`}
                    destructive={user.isAdmin}
                    onChange={handleUserChange}
                  />
                  <UserActionButton
                    user={user}
                    path="archive"
                    body={{ archived: true }}
                    label="Archive"
                    icon={ArchiveOutlinedIcon}
                    title={`Archive ${user.displayName}?`}
                    text="For people who left: they can't use the app any more and no longer show up in member pickers. This doesn't remove them from Telegram groups; use Kick in Channels → Manage for that. You can restore them from the Archived tab."
                    destructive
                    onChange={handleUserChange}
                  />
                </Box>
              );
            },
          },
        ]
      : []),
  ];
  return (
    <>
      {" "}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "75vh",
          }}
        >
          <Loading size={50} thickness={6} />
        </Box>
      ) : (
        <Container maxWidth={"xl"}>
          <Header title="Users" />
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <CustomTabsTable
                columns={columns}
                tabs={tabsFunctions}
                name={"adminUsers"}
                data={users}
              />
            </Grid>
          </Grid>
        </Container>
      )}
    </>
  );
}

export default AdminUsers;
